import type { MeetingDTO, OrganizationMethodDTO } from 'models/purchaseFlowRevamp'

import { compact } from './wire'

/**
 * Map the FE meeting DTO onto the polymorphic organization-method wire shape.
 * Emits the Jackson `type` discriminator and a wire-required
 * `timezone` for every mode. `userTimezone` (resolved by the caller via
 * `getUserTimezone` / `useTimezone`) is the fallback for the modes/cases the FE
 * forms don't collect a tz for. Kept pure — the caller injects the timezone.
 */
export function buildOrganizationMethod(
  meeting: MeetingDTO | undefined,
  userTimezone: string,
  locationTimezone?: string
): OrganizationMethodDTO | undefined {
  if (!meeting || meeting.mode === 'NONE') return undefined

  switch (meeting.mode) {
    case 'ON_SITE': {
      const f = meeting.onSite ?? {}
      // All or nothing. `compact` drops undefined keys, so a
      // half-filled meeting used to serialise as a DTO missing `name`/`date`
      // and the BE had to reject a shape the FE should never have built. Legacy
      // guarded the same way (`PurchaseFlowPlaceOrder.context.tsx` returned
      // nothing without name + date + place). The Next gate blocks these too;
      // this is the belt to that braces, and it keeps the serializer honest
      // when called from anywhere else.
      if (!f.contactName || !f.contactPhone || !f.date) return undefined
      return compact<OrganizationMethodDTO>({
        type: 'MEETING_ON_SITE',
        name: f.contactName,
        phone: f.contactPhone,
        email: f.contactEmail,
        comment: f.comment,
        date: f.date,
        // The instant was entered in the SHOOT's zone, so label it with that
        // zone; the client's own is only a fallback.
        timezone: locationTimezone || userTimezone,
      })
    }
    case 'KEY_PICKUP': {
      const f = meeting.keyPickup ?? {}
      // Coordinates included: the wire needs a routable pickup point, and only
      // a geocoded pick produces one.
      // A flexible pickup deliberately has no date: the BE reads its absence as
      // "the creative arranges the time".
      const date = f.arrangedByCreative ? undefined : f.date
      if (!f.contactName || !f.contactPhone || !f.address || !f.coordinates) return undefined
      if (!date && !f.arrangedByCreative) return undefined
      return compact<OrganizationMethodDTO>({
        type: 'KEYS_PICKUP',
        name: f.contactName,
        phone: f.contactPhone,
        email: f.contactEmail,
        comment: f.comment,
        address: f.address,
        // coordinates come from the geocoded pickup address, resolved natively
        // by `meeting.helpers.ts` from the `ReactGeocodePlace`; omitted only if absent.
        coordinates: f.coordinates,
        date,
        // Same as ON_SITE: the picker is bound to the shoot's zone.
        timezone: locationTimezone || userTimezone,
      })
    }
    case 'THIRD_PARTY': {
      const f = meeting.thirdParty ?? {}
      // Looser by design, mirroring the gate: a third party needs a name and one
      // way to reach them.
      if (!f.name || (!f.phone && !f.email)) return undefined
      return compact<OrganizationMethodDTO>({
        type: 'ORGANIZATION_THIRD_PARTY',
        name: f.name,
        phone: f.phone,
        email: f.email,
        // THIRD_PARTY collects tz on the FE; user-tz fallback if blank.
        timezone: f.timezone || userTimezone,
        comment: f.comment,
      })
    }
    default:
      return undefined
  }
}
